import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Send, Check, Undo2, BellRing, FileSpreadsheet, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { userHasCapability, compareFees, type FeeSortKey, type SortDir } from "@costadorada/shared";
import { api } from "@/lib/api";
import { formatCurrency, formatDate, formatDateOnly } from "@/lib/cn";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/Badge";
import { Modal } from "@/components/Modal";
import { RulePanel } from "@/components/RulePanel";
import { QueryError } from "@/components/QueryError";
import { AdminRecordActions } from "@/components/AdminRecordActions";

interface Fee {
  id: string;
  unitId: string;
  amount: string;
  dueDate: string;
  period: string;
  status: "PENDING" | "PAID" | "OVERDUE" | "PARTIAL";
  paidAt?: string | null;
  paymentMethod?: string | null;
  lateFeeAmount: number;
  lateFeeRate: number;
  amountPaid: number;
  balanceRemaining: number;
  totalDue: number;
  unit: { unitNumber: string; building?: string | null; ownerName: string; ownerEmail?: string | null };
  /** Recorded payments, oldest first. */
  payments?: Array<{ id: string; amount: string; method: string; reference?: string | null; paidAt: string }>;
  /** From the reminder log (manual and automatic reminders). */
  lastReminderAt?: string | null;
  reminderCount?: number;
}

const statusVariant: Record<string, "default" | "info" | "warning" | "success" | "danger"> = {
  PENDING: "info",
  PAID: "success",
  OVERDUE: "danger",
  PARTIAL: "warning",
};

// Shown at most per load; beyond it the summary says so and the Excel export has them all.
const PAGE_SIZE = 200;
const UNPAID = "PENDING,OVERDUE,PARTIAL";

export function FeesPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [genOpen, setGenOpen] = useState(false);
  const [payFor, setPayFor] = useState<Fee | null>(null);
  const [editing, setEditing] = useState<Fee | null>(null);
  const [remindFor, setRemindFor] = useState<Fee | null>(null);
  const [unpayFor, setUnpayFor] = useState<Fee | null>(null);

  // Filters: the most recent period to start with (once the periods are known), then any
  // status, building or search. They are applied by the server; the order is the screen's.
  const [period, setPeriod] = useState<string | null>(null);
  const [status, setStatus] = useState("");
  const [building, setBuilding] = useState("");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<{ key: FeeSortKey; dir: SortDir }>({ key: "unit", dir: "asc" });
  useEffect(() => {
    const h = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(h);
  }, [search]);

  const { data: options, isError: optionsError } = useQuery({
    queryKey: ["fee-filters"],
    queryFn: async () => (await api.get<{ periods: string[]; buildings: string[] }>("/maintenance-fees/filters")).data,
  });
  useEffect(() => {
    if (period === null && (options || optionsError)) setPeriod(options?.periods[0] ?? "");
  }, [options, optionsError, period]);

  const params = { period: period || undefined, status: status || undefined, building: building || undefined, q: q || undefined };
  const { data: page, isLoading, isError } = useQuery({
    queryKey: ["fees", params],
    queryFn: async () =>
      (await api.get<{ data: Fee[]; total: number }>("/maintenance-fees", { params: { ...params, page: 1, pageSize: PAGE_SIZE } })).data,
    enabled: period !== null,
    placeholderData: (prev) => prev,
  });
  const fees = useMemo(
    () => [...(page?.data ?? [])].sort((a, b) => compareFees(a, b, sort.key, sort.dir)),
    [page, sort]
  );
  const total = page?.total ?? 0;
  const balanceDue = fees.reduce((sum, f) => sum + f.balanceRemaining, 0);
  const toggleSort = (key: FeeSortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  // The same filters and order, as an Excel file with every matching fee.
  const exportXlsx = useMutation({
    mutationFn: async () => {
      const res = await api.get<Blob>("/maintenance-fees/export", { params: { ...params, sort: sort.key, dir: sort.dir }, responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(String(res.headers["content-disposition"] ?? ""))?.[1] ?? "cuotas.xlsx";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/maintenance-fees/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fees"] }),
  });

  const isBookkeeper = userHasCapability(user, "finance:manage");
  const canManage = user?.role === "ADMIN" || user?.role === "BOARD_MEMBER" || isBookkeeper;
  // Undoing a payment is more delicate than recording one: administration or bookkeeping.
  const canRevert = user?.role === "ADMIN" || isBookkeeper;
  const refresh = () => qc.invalidateQueries({ queryKey: ["fees"] });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("finance.fees")}</h1>
        <div className="flex flex-wrap gap-2">
          <Link to="/finance/reminders" className="btn-secondary">
            <BellRing size={16} /> {t("finance.remindersButton")}
          </Link>
          {(user?.role === "ADMIN" || isBookkeeper) && (
            <button className="btn-primary" onClick={() => setGenOpen(true)}>
              <Plus size={16} /> {t("finance.generateFees")}
            </button>
          )}
        </div>
      </div>

      {canManage && <RulePanel tags={["late-payment", "fine"]} />}

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <select className="input w-auto" aria-label={t("finance.period")} value={period ?? ""} onChange={(e) => setPeriod(e.target.value)}>
            <option value="">{t("finance.allPeriods")}</option>
            {options?.periods.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <select className="input w-auto" aria-label={t("finance.status")} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">{t("finance.allStatuses")}</option>
            <option value={UNPAID}>{t("finance.unpaidFilter")}</option>
            {(["PENDING", "OVERDUE", "PARTIAL", "PAID"] as const).map((s) => (
              <option key={s} value={s}>{t(`finance.${s.toLowerCase()}`)}</option>
            ))}
          </select>
          <select className="input w-auto" aria-label={t("finance.building")} value={building} onChange={(e) => setBuilding(e.target.value)}>
            <option value="">{t("finance.allBuildings")}</option>
            {options?.buildings.map((b) => (
              <option key={b} value={b}>{t("finance.building")} {b}</option>
            ))}
          </select>
          <input
            type="search"
            className="input max-w-xs"
            placeholder={t("finance.searchFees")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="button" className="btn-secondary ml-auto" disabled={exportXlsx.isPending || total === 0} onClick={() => exportXlsx.mutate()}>
            <FileSpreadsheet size={16} /> {exportXlsx.isPending ? t("finance.exporting") : t("finance.exportExcel")}
          </button>
        </div>
        {page && (
          <div className="text-xs text-slate-500">
            {t("finance.feesSummary", { count: total, balance: formatCurrency(balanceDue) })}
            {total > fees.length && (
              <span className="ml-2 text-amber-700">{t("finance.feesTruncated", { shown: fees.length, total })}</span>
            )}
          </div>
        )}
        {exportXlsx.isError && <div className="text-xs text-red-600">{t("finance.exportError")}</div>}
      </div>

      <div className="card p-0 overflow-x-auto">
        {isError ? (
          <div className="p-6"><QueryError /></div>
        ) : isLoading || period === null ? (
          <div className="p-6 text-slate-500">{t("common.loading")}</div>
        ) : fees.length === 0 ? (
          <div className="p-6 text-slate-500">—</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <SortHeader label={t("finance.unit")} k="unit" sort={sort} onSort={toggleSort} />
                <SortHeader label={t("finance.period")} k="period" sort={sort} onSort={toggleSort} />
                <SortHeader label={t("finance.amount")} k="amount" sort={sort} onSort={toggleSort} align="right" />
                <SortHeader label={t("finance.dueDate")} k="dueDate" sort={sort} onSort={toggleSort} />
                <SortHeader label={t("finance.status")} k="status" sort={sort} onSort={toggleSort} />
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {fees.map((f) => (
                <tr key={f.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <div className="font-medium">{f.unit.unitNumber}</div>
                    <div className="text-xs text-slate-500">{f.unit.ownerName}</div>
                  </td>
                  <td className="px-4 py-2">{f.period}</td>
                  <td className="px-4 py-2 text-right font-medium">
                    {formatCurrency(f.amount)}
                    {f.status === "PARTIAL" && (
                      <div className="text-xs text-amber-600 font-normal">
                        {t("finance.paidOf", { paid: formatCurrency(f.amountPaid), total: formatCurrency(f.amount) })}
                      </div>
                    )}
                    {f.lateFeeAmount > 0 && (
                      <div className="text-xs text-red-600 font-normal">
                        + {formatCurrency(f.lateFeeAmount)} {t("finance.lateFeeShort", { pct: Math.round(f.lateFeeRate * 100) })}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2">{formatDateOnly(f.dueDate, i18n.language)}</td>
                  <td className="px-4 py-2">
                    <Badge variant={statusVariant[f.status]}>{t(`finance.${f.status.toLowerCase()}`)}</Badge>
                    {!!f.reminderCount && f.lastReminderAt && (
                      <div className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500">
                        <BellRing size={11} />
                        {t("finance.reminderLast", { date: formatDate(f.lastReminderAt, i18n.language), count: f.reminderCount })}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex gap-2 justify-end items-center">
                      {canManage && f.status !== "PAID" && (
                        <>
                          <button
                            className="btn-secondary text-xs"
                            onClick={() => setRemindFor(f)}
                            disabled={!f.unit.ownerEmail}
                            title={!f.unit.ownerEmail ? t("finance.noEmail") : ""}
                          >
                            <Send size={12} /> {t("finance.sendReminder")}
                          </button>
                          <button className="btn-primary text-xs" onClick={() => setPayFor(f)}>
                            <Check size={12} /> {t("finance.markPaid")}
                          </button>
                        </>
                      )}
                      {canRevert && (f.status === "PAID" || f.status === "PARTIAL") && (
                        <button className="btn-secondary text-xs" onClick={() => setUnpayFor(f)}>
                          <Undo2 size={12} /> {t("finance.unpay")}
                        </button>
                      )}
                      <AdminRecordActions
                        compact
                        entityLabel={`${f.unit.unitNumber} · ${f.period}`}
                        onEdit={() => setEditing(f)}
                        onDelete={() => deleteMutation.mutate(f.id)}
                        disabled={deleteMutation.isPending}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <GenerateFeesModal open={genOpen} onClose={() => setGenOpen(false)} onDone={() => qc.invalidateQueries({ queryKey: ["fees"] })} />
      <MarkPaidModal key={payFor?.id ?? "none"} fee={payFor} onClose={() => setPayFor(null)} onDone={() => qc.invalidateQueries({ queryKey: ["fees"] })} />
      {remindFor && <ReminderModal key={remindFor.id} fee={remindFor} onClose={() => setRemindFor(null)} onDone={refresh} />}
      {unpayFor && <UnpayModal key={unpayFor.id} fee={unpayFor} onClose={() => setUnpayFor(null)} onDone={refresh} />}
      {editing && (
        <EditFeeModal key={editing.id} fee={editing} onClose={() => setEditing(null)} onDone={() => qc.invalidateQueries({ queryKey: ["fees"] })} />
      )}
    </div>
  );
}

/** A column header that sorts the list: click to sort by it, click again to reverse. */
function SortHeader({
  label,
  k,
  sort,
  onSort,
  align = "left",
}: {
  label: string;
  k: FeeSortKey;
  sort: { key: FeeSortKey; dir: SortDir };
  onSort: (k: FeeSortKey) => void;
  align?: "left" | "right";
}) {
  const active = sort.key === k;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-4 py-2 ${align === "right" ? "text-right" : "text-left"}`} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" className={`inline-flex items-center gap-1 hover:text-primary ${active ? "text-slate-900" : ""}`} onClick={() => onSort(k)}>
        {label}
        <Icon size={12} className={active ? "" : "text-slate-400"} />
      </button>
    </th>
  );
}

function EditFeeModal({ fee, onClose, onDone }: { fee: Fee | null; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    amount: fee ? String(fee.amount) : "",
    dueDate: fee ? fee.dueDate.slice(0, 10) : "",
    period: fee?.period ?? "",
  });

  const mutation = useMutation({
    mutationFn: async () =>
      (await api.put(`/maintenance-fees/${fee!.id}`, {
        amount: parseFloat(form.amount),
        dueDate: form.dueDate,
        period: form.period,
      })).data,
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  if (!fee) return null;

  return (
    <Modal open={!!fee} onClose={onClose} title={`${t("common.edit")} — ${fee.unit.unitNumber} · ${fee.period}`}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <div>
          <label className="label">{t("finance.period")} (YYYY-MM)</label>
          <input className="input" required value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} />
        </div>
        <div>
          <label className="label">{t("finance.amount")} (USD)</label>
          <input type="number" step="0.01" className="input" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        </div>
        <div>
          <label className="label">{t("finance.dueDate")}</label>
          <input type="date" className="input" required value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
        </div>
        {mutation.isError && (
          <div className="text-sm text-red-600">{(mutation.error as any)?.response?.data?.message ?? t("common.error")}</div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn-primary" disabled={mutation.isPending}>{t("common.save")}</button>
        </div>
      </form>
    </Modal>
  );
}

function GenerateFeesModal({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const now = new Date();
  const [form, setForm] = useState({
    dueDate: new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString().slice(0, 10),
    period: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`,
  });
  // The modal stays open on success so the admin sees what was billed and which units
  // were left out for lack of a monthly fee.
  const mutation = useMutation({
    mutationFn: async () =>
      (await api.post<{ created: number; skipped: number; missingFee: string[] }>("/maintenance-fees/bulk-generate", form)).data,
    onSuccess: () => onDone(),
  });
  const close = () => {
    mutation.reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={close} title={t("finance.generateFees")}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <div>
          <label className="label">{t("finance.period")} (YYYY-MM)</label>
          <input className="input" required value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} />
        </div>
        <p className="text-xs text-slate-500">{t("finance.generateFeesHint")}</p>
        <div>
          <label className="label">{t("finance.dueDate")}</label>
          <input type="date" className="input" required value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
        </div>
        {mutation.isError && (
          <div className="text-sm text-red-600">{(mutation.error as any)?.response?.data?.message ?? t("common.error")}</div>
        )}
        {mutation.isSuccess && (
          <div className="space-y-2">
            <div className="text-sm text-green-700">
              {t("finance.generateFeesResult", { created: mutation.data.created, skipped: mutation.data.skipped })}
            </div>
            {mutation.data.missingFee.length > 0 && (
              <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                {t("finance.generateFeesMissing", { count: mutation.data.missingFee.length })}{" "}
                <span className="font-medium">{mutation.data.missingFee.join(", ")}</span>
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={close}>
            {mutation.isSuccess ? t("common.close") : t("common.cancel")}
          </button>
          {!mutation.isSuccess && (
            <button type="submit" className="btn-primary" disabled={mutation.isPending}>{t("finance.generate")}</button>
          )}
        </div>
      </form>
    </Modal>
  );
}

function MarkPaidModal({ fee, onClose, onDone }: { fee: Fee | null; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [paymentMethod, setPaymentMethod] = useState("Transferencia");
  const [transactionRef, setTransactionRef] = useState("");
  const [amount, setAmount] = useState(fee ? String(fee.balanceRemaining) : "");

  const mutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/maintenance-fees/${fee!.id}/pay`, { paymentMethod, transactionRef, amount: parseFloat(amount) })).data,
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  if (!fee) return null;

  const enteredAmount = parseFloat(amount) || 0;
  const isPartial = enteredAmount > 0 && enteredAmount < fee.balanceRemaining - 0.01;

  return (
    <Modal open={!!fee} onClose={onClose} title={`${t("finance.markPaid")} — ${fee.unit.unitNumber} · ${fee.period}`}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <div className="bg-slate-50 p-3 rounded text-sm space-y-1">
          <div>
            {t("finance.amount")}: <strong>{formatCurrency(fee.amount)}</strong>
          </div>
          {fee.amountPaid > 0 && (
            <div className="text-green-700">
              {t("finance.alreadyPaid")}: <strong>{formatCurrency(fee.amountPaid)}</strong>
            </div>
          )}
          {fee.lateFeeAmount > 0 && (
            <div className="text-red-600">
              {t("finance.lateFee", { pct: Math.round(fee.lateFeeRate * 100) })}: <strong>{formatCurrency(fee.lateFeeAmount)}</strong>
            </div>
          )}
          <div>
            {t("finance.balanceRemaining")}: <strong>{formatCurrency(fee.balanceRemaining)}</strong>
          </div>
        </div>
        <div>
          <label className="label">{t("finance.paymentAmount")} (USD)</label>
          <input type="number" step="0.01" min="0.01" className="input" required value={amount} onChange={(e) => setAmount(e.target.value)} />
          {isPartial && <p className="text-xs text-amber-600 mt-1">{t("finance.partialPaymentNote")}</p>}
        </div>
        <div>
          <label className="label">{t("finance.paymentMethod")}</label>
          <select className="input" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
            <option>Transferencia</option>
            <option>ATH Móvil</option>
            <option>Cheque</option>
            <option>Efectivo</option>
            <option>Tarjeta</option>
          </select>
        </div>
        <div>
          <label className="label">{t("finance.transactionRef")}</label>
          <input className="input" value={transactionRef} onChange={(e) => setTransactionRef(e.target.value)} />
        </div>
        {mutation.isError && (
          <div className="text-sm text-red-600">{(mutation.error as any)?.response?.data?.message ?? t("common.error")}</div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn-primary" disabled={mutation.isPending}>{t("common.save")}</button>
        </div>
      </form>
    </Modal>
  );
}

interface ReminderPreview {
  to: string;
  /** Each address it goes to: the owner's two emails when there are two. */
  toList?: string[];
  subject: string;
  html: string;
  history: Array<{ sentAt: string; by: string | null; automatic: boolean; to: string | null; note: string | null }>;
}

/**
 * "Enviar Recordatorio": the exact email first — recipient, subject and body as the owner
 * will see it — with an optional note of the administration's own and the reminders
 * already sent for this fee. Nothing goes out until "Enviar".
 */
function ReminderModal({ fee, onClose, onDone }: { fee: Fee; onClose: () => void; onDone: () => void }) {
  const { t, i18n } = useTranslation();
  const [note, setNote] = useState("");
  const [typedNote, setTypedNote] = useState("");
  useEffect(() => {
    const h = setTimeout(() => setTypedNote(note.trim()), 400);
    return () => clearTimeout(h);
  }, [note]);
  const { data: preview, isError, error } = useQuery({
    queryKey: ["fee-reminder-preview", fee.id, typedNote],
    queryFn: async () =>
      (await api.get<ReminderPreview>(`/maintenance-fees/${fee.id}/reminder-preview`, { params: { note: typedNote || undefined } })).data,
    placeholderData: (prev) => prev,
  });
  const [sent, setSent] = useState<{ to: string; sentAt: string } | null>(null);
  const send = useMutation({
    mutationFn: async () => (await api.post(`/maintenance-fees/${fee.id}/remind`, { note: note.trim() || null })).data,
    onSuccess: (d: { to: string; sentAt: string }) => {
      setSent(d);
      onDone();
    },
  });

  return (
    <Modal open onClose={onClose} title={`${t("finance.reminderTitle")} — ${fee.unit.unitNumber} · ${fee.period}`} size="lg">
      {sent ? (
        <div className="space-y-3 text-sm">
          <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-green-800">
            {t("finance.reminderSent", { to: sent.to, date: formatDate(sent.sentAt, i18n.language) })}
          </div>
          <div className="flex justify-end">
            <button className="btn-primary" onClick={onClose}>{t("common.close")}</button>
          </div>
        </div>
      ) : (
        <div className="space-y-3 text-sm">
          {isError ? (
            <div className="text-red-600">{(error as any)?.response?.data?.message ?? t("common.error")}</div>
          ) : !preview ? (
            <div className="text-slate-500">{t("common.loading")}</div>
          ) : (
            <>
              <div className="grid gap-1">
                <div className="flex flex-wrap items-center gap-1">
                  <span className="text-slate-500">{t("finance.reminderTo")}:</span>
                  {(preview.toList ?? [preview.to]).map((e) => (
                    <span key={e} className="rounded bg-slate-100 px-2 py-0.5 text-slate-800">{e}</span>
                  ))}
                  {(preview.toList?.length ?? 1) > 1 && (
                    <span className="text-xs text-slate-500">{t("finance.reminderBothEmails", { count: preview.toList!.length })}</span>
                  )}
                </div>
                <div><span className="text-slate-500">{t("finance.reminderSubject")}:</span> {preview.subject}</div>
              </div>
              {/* The email exactly as it will arrive; sandboxed, so nothing in it can run. */}
              <iframe
                title={t("finance.reminderTitle")}
                sandbox=""
                srcDoc={`<div style="font-family:system-ui,-apple-system,Segoe UI,sans-serif;font-size:14px;line-height:1.5;color:#1e293b">${preview.html}</div>`}
                className="h-72 w-full rounded border border-slate-200 bg-white"
              />
            </>
          )}
          <div>
            <label className="label">{t("finance.reminderNote")}</label>
            <textarea className="input min-h-[70px]" maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
            <p className="mt-1 text-xs text-slate-500">{t("finance.reminderNoteHint")}</p>
          </div>
          {preview && (
            preview.history.length > 0 ? (
              <div>
                <div className="text-xs font-medium text-slate-600">{t("finance.reminderHistory", { count: preview.history.length })}</div>
                <ul className="mt-1 max-h-32 space-y-0.5 overflow-y-auto text-xs text-slate-600">
                  {preview.history.map((h, i) => (
                    <li key={i}>
                      {formatDate(h.sentAt, i18n.language)} · {h.automatic ? t("finance.reminderAutomatic") : h.by ?? "—"}
                      {h.to ? ` · ${h.to}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-xs text-slate-500">{t("finance.reminderNone")}</p>
            )
          )}
          {send.isError && <div className="text-red-600">{(send.error as any)?.response?.data?.message ?? t("common.error")}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
            <button type="button" className="btn-primary" disabled={send.isPending || !preview} onClick={() => send.mutate()}>
              <Send size={14} /> {t("finance.reminderSend")}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/**
 * "Quitar pago": undoes the last recorded payment (one entered by mistake), saying first
 * which payment goes and what the fee becomes.
 */
function UnpayModal({ fee, onClose, onDone }: { fee: Fee; onClose: () => void; onDone: () => void }) {
  const { t, i18n } = useTranslation();
  const payments = fee.payments ?? [];
  const last = payments.length ? payments[payments.length - 1] : null;
  const stillPaid = payments.slice(0, -1).reduce((sum, p) => sum + parseFloat(p.amount), 0);
  const overdue = fee.dueDate.slice(0, 10) < new Date().toISOString().slice(0, 10);
  const result = stillPaid > 0 ? "PARTIAL" : overdue ? "OVERDUE" : "PENDING";
  const [reason, setReason] = useState("");
  const mutation = useMutation({
    mutationFn: async () => (await api.post(`/maintenance-fees/${fee.id}/unpay`, { reason: reason.trim() || null })).data,
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  return (
    <Modal open onClose={onClose} title={`${t("finance.unpay")} — ${fee.unit.unitNumber} · ${fee.period}`}>
      <div className="space-y-3 text-sm">
        {last ? (
          <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="text-xs text-slate-500">{t("finance.unpayWillRemove")}</div>
            <div className="mt-1 font-medium">{formatCurrency(last.amount)} · {last.method}</div>
            <div className="text-xs text-slate-600">
              {formatDate(last.paidAt, i18n.language)}
              {last.reference ? ` · ${t("finance.transactionRef")}: ${last.reference}` : ""}
            </div>
          </div>
        ) : (
          <p className="text-slate-600">{t("finance.unpayNoRow")}</p>
        )}
        <p>{t("finance.unpayResult", { status: t(`finance.${result.toLowerCase()}`) })}</p>
        <div>
          <label className="label">{t("finance.unpayReason")}</label>
          <textarea className="input min-h-[60px]" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        {mutation.isError && <div className="text-red-600">{(mutation.error as any)?.response?.data?.message ?? t("common.error")}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
          <button type="button" className="btn bg-red-600 text-white hover:bg-red-700" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            <Undo2 size={14} /> {t("finance.unpayConfirm")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
